import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Form, message } from 'antd';
import { useAuthContext } from '../../contexts/AuthContext';
import { outOfNavRoutes } from '../../utils/constants/adminRoutes';
import PasswordConfirmFields from './PasswordConfirmFields';

/**
 * ChangePwdForm component for handling password change.
 *
 * @param {object} props - The component props.
 * @param {string} props.token - The reset token for changing the password.
 * @param {Function} props.switchForm - Function to switch to another form.
 *
 * @returns {ReactElement} The rendered ChangePwdForm component.
 */
const ChangePwdForm = ({ token, switchForm }) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);

  const changePwd = async (body) => {
    setLoading(true);
    try {
      await dispatchAPI('POST', {
        url: `reset-pwd/${encodeURIComponent(token)}`,
        body
      });
      message.success(t('login.passwordChanged'));
      switchForm('login');
      navigate(outOfNavRoutes.LOGIN);
    } catch {
      message.error(t('login.wrongToken'));
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    await changePwd(values);
  };

  return (
    <Form onFinish={handleSubmit}>
      <PasswordConfirmFields
        submitLabel={t('login.resetPwd')}
        loading={isLoading}
      />
    </Form>
  );
};

ChangePwdForm.propTypes = {
  token: PropTypes.string,
  switchForm: PropTypes.func.isRequired
};

ChangePwdForm.defaultProps = {
  token: null
};

export default ChangePwdForm;
